import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { BONK, SKR, MEW, USDC, ANGELIC_SOL, GMT, DRAGON } from '@/lib/constants';

interface TokenPrice {
  symbol: string;
  mint: string;
  price: number | null;
  change24h: number | null;
  loading: boolean;
  error: boolean;
  /** 'up' | 'down' | null — cleared after flash duration */
  flash: 'up' | 'down' | null;
}

const TOKENS: Omit<TokenPrice, 'price' | 'change24h' | 'loading' | 'error' | 'flash'>[] = [
  { symbol: 'SOL',   mint: 'So11111111111111111111111111111111111111112' },
  { symbol: 'BONK',  mint: BONK },
  { symbol: 'SKR',   mint: SKR },
  { symbol: 'MEW',   mint: MEW },
  { symbol: 'USDC',  mint: USDC },
  { symbol: 'ANGEL', mint: ANGELIC_SOL },
  { symbol: 'GMT',   mint: GMT },
  { symbol: 'DRAGON', mint: DRAGON },
];

const TOKEN_COLORS: Record<string, { color: string; bg: string; border: string }> = {
  SOL: {
    color: 'hsl(43 75% 68%)',
    bg: 'hsl(38 65% 57% / 0.10)',
    border: 'hsl(38 65% 57% / 0.25)',
  },
  BONK: {
    color: 'hsl(36 92% 55%)',
    bg: 'hsl(36 85% 50% / 0.10)',
    border: 'hsl(36 85% 50% / 0.25)',
  },
  SKR: {
    color: 'hsl(168 80% 48%)',
    bg: 'hsl(168 70% 42% / 0.10)',
    border: 'hsl(168 70% 42% / 0.25)',
  },
  MEW: {
    color: 'hsl(280 75% 65%)',
    bg: 'hsl(280 65% 55% / 0.10)',
    border: 'hsl(280 65% 55% / 0.25)',
  },
  USDC: {
    color: 'hsl(214 72% 60%)',
    bg: 'hsl(214 65% 52% / 0.10)',
    border: 'hsl(214 65% 52% / 0.25)',
  },
  ANGEL: {
    color: 'hsl(280 80% 72%)',
    bg: 'hsl(280 70% 60% / 0.10)',
    border: 'hsl(280 70% 60% / 0.25)',
  },
  GMT: {
    color: 'hsl(142 65% 55%)',
    bg: 'hsl(142 55% 45% / 0.10)',
    border: 'hsl(142 55% 45% / 0.25)',
  },
  DRAGON: {
    color: 'hsl(8 85% 58%)',
    bg: 'hsl(8 75% 48% / 0.10)',
    border: 'hsl(8 75% 48% / 0.25)',
  },
};

const TOKEN_LABELS: Record<string, string> = {
  SOL: 'Solana',
  BONK: 'Bonk',
  SKR: 'Seeker',
  MEW: 'cat in a dogs world',
  USDC: 'USD Coin',
  ANGEL: 'AngelicSol',
  GMT: 'STEPN',
  DRAGON: 'DRAGON',
};

/** Format price with appropriate decimals depending on magnitude */
function formatPrice(price: number): string {
  if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return price.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  if (price >= 0.01) return price.toFixed(5);
  if (price >= 0.0001) return price.toFixed(7);
  return price.toFixed(8);
}

// Primary poll interval — 5 seconds
const FAST_INTERVAL_MS = 5_000;
// Back-off interval on rate-limit — 10 seconds
const BACKOFF_INTERVAL_MS = 10_000;
// How long to show a flash color — 1 second
const FLASH_DURATION_MS = 1_000;

export const PriceTicker: React.FC = () => {
  const [prices, setPrices] = useState<TokenPrice[]>(
    TOKENS.map((t) => ({ ...t, price: null, change24h: null, loading: true, error: false, flash: null }))
  );
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isLive, setIsLive] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const flashTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const prevPricesRef = useRef<Map<string, number>>(new Map());
  const backoffRef = useRef(false);

  const clearFlashTimer = useCallback((symbol: string) => {
    const existing = flashTimersRef.current.get(symbol);
    if (existing !== undefined) {
      clearTimeout(existing);
      flashTimersRef.current.delete(symbol);
    }
  }, []);

  /** Apply a batch of new prices (keyed by symbol) and update flash/prev state */
  const applyPrices = useCallback(
    (
      prev: TokenPrice[],
      priceMap: Map<string, number | null>,
      changeMap: Map<string, number | null>,
    ): TokenPrice[] => {
      return TOKENS.map((t, i) => {
        const newPrice = priceMap.get(t.symbol) ?? null;
        const change24h = changeMap.get(t.symbol) ?? prev[i]?.change24h ?? null;
        const oldPrice = prevPricesRef.current.get(t.symbol) ?? null;

        let flash: 'up' | 'down' | null = null;
        if (newPrice !== null && oldPrice !== null && newPrice !== oldPrice) {
          flash = newPrice > oldPrice ? 'up' : 'down';
          prevPricesRef.current.set(t.symbol, newPrice);
          clearFlashTimer(t.symbol);
          const timer = setTimeout(() => {
            setPrices((p) =>
              p.map((item) => (item.symbol === t.symbol ? { ...item, flash: null } : item))
            );
            flashTimersRef.current.delete(t.symbol);
          }, FLASH_DURATION_MS);
          flashTimersRef.current.set(t.symbol, timer);
        } else if (newPrice !== null) {
          prevPricesRef.current.set(t.symbol, newPrice);
        }

        return {
          ...t,
          price: newPrice,
          change24h,
          loading: false,
          error: newPrice === null,
          flash,
        };
      });
    },
    [clearFlashTimer],
  );

  const fetchPrices = useCallback(async () => {
    // Primary: Jupiter Price API v3 lite (free, no key required). v2 was retired
    // (returns HTTP 404) — v3 is a flat map keyed by mint, not nested under `data`.
    const jupIds = TOKENS.map((t) => t.mint).join(',');
    const jupUrl = `https://lite-api.jup.ag/price/v3?ids=${jupIds}`;

    try {
      const res = await fetch(jupUrl, { signal: AbortSignal.timeout(8000) });

      // Handle rate-limiting — back off
      if (res.status === 429) {
        if (!backoffRef.current) {
          backoffRef.current = true;
          console.warn('[PriceTicker] Jupiter rate-limited — backing off to 10s interval');
          if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => void fetchPrices(), BACKOFF_INTERVAL_MS);
          }
        }
        throw new Error('rate-limited');
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Restore fast interval after successful response
      if (backoffRef.current) {
        backoffRef.current = false;
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = setInterval(() => void fetchPrices(), FAST_INTERVAL_MS);
        }
      }

      const json = await res.json();
      // Jupiter v3 response: flat top-level map keyed by mint (no `data` wrapper),
      // e.g. { [mint]: { usdPrice: 0.0000038387411021294245, priceChange24h: 1.829,
      // decimals, liquidity, blockId, createdAt, launchpad? } }. Confirmed by a live
      // fetch — `usdPrice` and `priceChange24h` come back as real JSON numbers, not
      // strings, so no parseFloat is needed. `priceChange24h` is sometimes absent
      // entirely (e.g. thin pump.fun markets) — treat that as null, not 0.
      const data: Record<string, { usdPrice?: number; priceChange24h?: number }> = json ?? {};

      const priceMap = new Map<string, number | null>();
      const changeMap = new Map<string, number | null>();
      for (const t of TOKENS) {
        const entry = data[t.mint];
        const rawPrice = entry?.usdPrice;
        priceMap.set(t.symbol, typeof rawPrice === 'number' && isFinite(rawPrice) ? rawPrice : null);
        const rawChange = entry?.priceChange24h;
        changeMap.set(t.symbol, typeof rawChange === 'number' && isFinite(rawChange) ? rawChange : null);
      }

      setPrices((prev) => applyPrices(prev, priceMap, changeMap));
      setLastUpdated(new Date());
      setIsLive(true);
      return; // success — don't fall through to DexScreener
    } catch (_jupiterError) {
      // Jupiter failed — fall through to DexScreener
    }

    // Fallback: DexScreener free API (no auth, CORS-friendly)
    try {
      // DexScreener: fetch each token individually (batching by comma-sep mints supported)
      const mints = TOKENS.map((t) => t.mint).join(',');
      const dsUrl = `https://api.dexscreener.com/latest/dex/tokens/${mints}`;
      const res = await fetch(dsUrl, { signal: AbortSignal.timeout(10000) });

      if (!res.ok) throw new Error(`DexScreener HTTP ${res.status}`);

      const json = await res.json();
      // Response: { pairs: [ { baseToken: { address }, priceUsd, priceChange: { h24 } }, ... ] }
      const pairs: Array<{
        baseToken?: { address?: string };
        priceUsd?: string;
        priceChange?: { h24?: number };
        volume?: { h24?: number };
      }> = json?.pairs ?? [];

      // For each mint, find the highest-volume pair to get best price
      const priceMap = new Map<string, number | null>();
      const changeMap = new Map<string, number | null>();

      for (const t of TOKENS) {
        const tokenPairs = pairs.filter(
          (p) => p.baseToken?.address?.toLowerCase() === t.mint.toLowerCase()
        );
        // Pick highest volume pair
        const best = tokenPairs.sort(
          (a, b) => (b.volume?.h24 ?? 0) - (a.volume?.h24 ?? 0)
        )[0];
        priceMap.set(t.symbol, best?.priceUsd ? parseFloat(best.priceUsd) : null);
        changeMap.set(t.symbol, best?.priceChange?.h24 ?? null);
      }

      setPrices((prev) => applyPrices(prev, priceMap, changeMap));
      setLastUpdated(new Date());
      setIsLive(true);
    } catch (_dsError) {
      // Both sources failed — mark all as error without clobbering existing prices
      setPrices((prev) =>
        prev.map((p) => ({ ...p, loading: false, error: true }))
      );
    }
  }, [applyPrices]);

  useEffect(() => {
    void fetchPrices();
    intervalRef.current = setInterval(() => void fetchPrices(), FAST_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      // Clear all pending flash timers
      for (const timer of flashTimersRef.current.values()) {
        clearTimeout(timer);
      }
      flashTimersRef.current.clear();
    };
  }, [fetchPrices]);

  return (
    <div
      className="relative w-full py-4 overflow-hidden"
      style={{
        background: 'hsl(30 6% 6% / 0.9)',
        borderTop: '1px solid hsl(35 12% 14%)',
        borderBottom: '1px solid hsl(35 12% 14%)',
      }}
    >
      {/* Subtle ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 100% at 50% 0%, hsl(38 65% 57% / 0.04), transparent)',
        }}
      />

      <div className="container relative z-10">
        {/* Section label */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* LIVE pulse dot */}
            <span className="relative flex items-center justify-center w-3 h-3">
              {isLive && (
                <>
                  <span
                    className="absolute inline-flex w-full h-full rounded-full animate-ping"
                    style={{ background: 'hsl(142 60% 48% / 0.5)' }}
                  />
                  <span
                    className="relative inline-flex rounded-full w-2 h-2"
                    style={{ background: 'hsl(142 60% 48%)' }}
                  />
                </>
              )}
              {!isLive && (
                <span
                  className="inline-flex rounded-full w-2 h-2"
                  style={{ background: 'hsl(35 12% 24%)' }}
                />
              )}
            </span>
            <span
              className="text-xs font-medium tracking-widest uppercase"
              style={{ color: isLive ? 'hsl(142 50% 50%)' : 'hsl(35 8% 42%)' }}
            >
              {isLive ? 'Live' : 'Loading'}
            </span>
          </div>
          {lastUpdated && (
            <span className="text-xs" style={{ color: 'hsl(35 8% 32%)' }}>
              Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>

        {/* Token grid — 2 cols on mobile, 3 on md, 6 on xl */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {prices.map((token) => {
            const palette = TOKEN_COLORS[token.symbol] ?? TOKEN_COLORS.SOL;
            const isPositive = (token.change24h ?? 0) > 0;
            const isNegative = (token.change24h ?? 0) < 0;

            // Flash overlay color
            const flashBg =
              token.flash === 'up'
                ? 'hsl(142 60% 48% / 0.10)'
                : token.flash === 'down'
                ? 'hsl(0 65% 52% / 0.10)'
                : undefined;

            const flashBorder =
              token.flash === 'up'
                ? 'hsl(142 60% 48% / 0.55)'
                : token.flash === 'down'
                ? 'hsl(0 65% 52% / 0.55)'
                : palette.border;

            // Animated price color on flash
            const priceColor =
              token.flash === 'up'
                ? 'hsl(142 60% 52%)'
                : token.flash === 'down'
                ? 'hsl(0 65% 58%)'
                : palette.color;

            return (
              <div
                key={token.symbol}
                className="relative overflow-hidden rounded-lg px-4 py-3 flex items-center gap-3 transition-all duration-300 hover:scale-[1.01]"
                style={{
                  background: flashBg ?? palette.bg,
                  border: `1px solid ${flashBorder}`,
                  transition: 'background 0.3s ease, border-color 0.3s ease',
                }}
              >
                {/* Token symbol badge */}
                <div
                  className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold"
                  style={{
                    background: palette.bg,
                    border: `1px solid ${palette.border}`,
                    color: palette.color,
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: '0.65rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  {token.symbol}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Price */}
                  {token.loading ? (
                    <div
                      className="h-4 w-20 rounded animate-pulse mb-1"
                      style={{ background: 'hsl(35 12% 14%)' }}
                    />
                  ) : token.error || token.price === null ? (
                    <span
                      className="text-sm font-mono font-semibold"
                      style={{ color: 'hsl(35 8% 38%)' }}
                    >
                      —
                    </span>
                  ) : (
                    <span
                      className="text-sm font-mono font-semibold leading-none"
                      style={{
                        color: priceColor,
                        transition: 'color 0.3s ease',
                      }}
                    >
                      ${formatPrice(token.price)}
                    </span>
                  )}

                  {/* 24h change */}
                  {!token.loading && token.change24h !== null && (
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {isPositive ? (
                        <TrendingUp size={10} style={{ color: 'hsl(142 60% 48%)' }} />
                      ) : isNegative ? (
                        <TrendingDown size={10} style={{ color: 'hsl(0 65% 52%)' }} />
                      ) : (
                        <Minus size={10} style={{ color: 'hsl(35 8% 42%)' }} />
                      )}
                      <span
                        className="text-xs font-mono"
                        style={{
                          color: isPositive
                            ? 'hsl(142 60% 48%)'
                            : isNegative
                            ? 'hsl(0 65% 52%)'
                            : 'hsl(35 8% 42%)',
                        }}
                      >
                        {isPositive ? '+' : ''}
                        {token.change24h.toFixed(2)}%
                      </span>
                    </div>
                  )}

                  {/* Label */}
                  <p
                    className="text-xs mt-0.5 truncate"
                    style={{ color: 'hsl(35 8% 38%)', fontSize: '0.65rem' }}
                  >
                    {TOKEN_LABELS[token.symbol] ?? token.symbol}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PriceTicker;
